import { useEffect, useRef, useState } from 'react';

// A number that tweens to its target instead of snapping — the app-wide "rolling counter" used by
// stat tiles, XP, streaks, leaderboard figures, mastery percentages, etc. Whenever `value` changes
// (including the first mount, which counts up from `from`) it animates over `duration` ms with an
// ease-out curve. Honours `prefers-reduced-motion`: those users get the final value with no motion.

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

// One matchMedia read, shared by every instance. Guarded because jsdom (tests) has no matchMedia.
// Exported so other hand-rolled tweens (e.g. MasteryRadar's polygon reshape) honour the same
// preference without re-reading matchMedia themselves.
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = (): void => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

interface AnimatedNumberProps {
  // The target value. The rendered figure eases toward this whenever it changes.
  value: number;
  // Maps the (fractional, mid-tween) number to the string shown — e.g. `n => `${Math.round(n)}%``.
  // Must round, since `value` is interpolated through non-integers during the animation.
  format?: (n: number) => string;
  // Tween length in ms.
  duration?: number;
  // Where the very first mount counts up from (default 0, for the dashboard "count-in" feel).
  from?: number;
  className?: string;
}

export function AnimatedNumber({
  value,
  format = (n) => String(Math.round(n)),
  duration = 700,
  from = 0,
  className,
}: AnimatedNumberProps) {
  const reduced = usePrefersReducedMotion();
  // The figure currently painted. Starts at `from` so the first render eases up into `value`
  // (reduced-motion users start already at `value` — no count-in).
  const [display, setDisplay] = useState(reduced ? value : from);
  // Mirror `display` into a ref so the tween can read "where we are now" as its start point without
  // re-subscribing the effect every frame (which would restart the animation endlessly).
  const displayRef = useRef(display);
  displayRef.current = display;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Reduced motion (or an already-arrived value): jump, don't animate.
    if (reduced) {
      setDisplay(value);
      return;
    }
    const startValue = displayRef.current;
    const delta = value - startValue;
    if (delta === 0) return;

    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      setDisplay(startValue + delta * easeOutCubic(t));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // `display` is read via the ref on purpose — it must not be a dependency or every frame restarts.
  }, [value, reduced, duration]);

  // tabular-nums keeps the width stable as digits roll, so surrounding layout never jitters.
  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {format(display)}
    </span>
  );
}
